if (!customElements.get('auto-scroll-row')) {
  class AutoScrollRow extends HTMLElement {
    connectedCallback() {
      this.slider = document.getElementById(this.dataset.target);
      if (!this.slider) return;
      this.wrapper = this.slider.closest('slider-component') || this.slider.parentElement;
      this.speed = parseFloat(this.dataset.speed) || 20;
      this.originals = Array.from(this.slider.children);
      this.clones = [];
      this.userPaused = false;
      this.inView = true;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.enableMobile = this.dataset.mobile !== 'false';
      this.enableDesktop = this.dataset.desktop === 'true';
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleChange = () => this.setup();
      this.mobileQuery.addEventListener('change', this.handleChange);
      this.motionQuery.addEventListener('change', this.handleChange);

      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
      });
      this.intersectionObserver.observe(this.slider);

      /* 指で触れたら止めて、自分でスクロールできるようにする */
      this.pauseForUser = () => {
        clearTimeout(this.resumeTimer);
        this.userPaused = true;
        if (this.slider) this.slider.style.setProperty('--auto-scroll-frac', '0px');
      };
      this.resumeLater = () => {
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(() => {
          this.position = this.slider.scrollLeft;
          this.userPaused = false;
        }, 2000);
      };
      this.handleScroll = () => {
        if (!this.userPaused) return;
        this.wrapPosition(this.slider.scrollLeft, true);
      };
      this.slider.addEventListener('touchstart', this.pauseForUser, { passive: true });
      this.slider.addEventListener('touchend', this.resumeLater, { passive: true });
      this.slider.addEventListener('touchcancel', this.resumeLater, { passive: true });
      this.slider.addEventListener('scroll', this.handleScroll, { passive: true });
      /* PCではマウスを乗せている間は止めて、商品を選びやすくする */
      this.handleMouseEnter = (event) => {
        if (event.pointerType === 'mouse') this.pauseForUser();
      };
      this.handleMouseLeave = (event) => {
        if (event.pointerType === 'mouse') this.resumeLater();
      };
      this.wrapper.addEventListener('pointerenter', this.handleMouseEnter);
      this.wrapper.addEventListener('pointerleave', this.handleMouseLeave);
      this.slider.addEventListener('focusin', this.pauseForUser);
      this.slider.addEventListener('focusout', this.resumeLater);

      this.setup();
    }

    disconnectedCallback() {
      this.teardown();
      clearTimeout(this.resumeTimer);
      if (!this.slider) return;
      this.mobileQuery.removeEventListener('change', this.handleChange);
      this.motionQuery.removeEventListener('change', this.handleChange);
      this.intersectionObserver.disconnect();
      this.slider.removeEventListener('touchstart', this.pauseForUser);
      this.slider.removeEventListener('touchend', this.resumeLater);
      this.slider.removeEventListener('touchcancel', this.resumeLater);
      this.slider.removeEventListener('scroll', this.handleScroll);
      this.wrapper.removeEventListener('pointerenter', this.handleMouseEnter);
      this.wrapper.removeEventListener('pointerleave', this.handleMouseLeave);
      this.slider.removeEventListener('focusin', this.pauseForUser);
      this.slider.removeEventListener('focusout', this.resumeLater);
    }

    teardown() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.clones.forEach((clone) => clone.remove());
      this.clones = [];
      if (this.wrapper) this.wrapper.classList.remove('is-auto-scrolling');
      if (this.slider) {
        this.slider.style.scrollSnapType = '';
        this.slider.style.scrollBehavior = '';
        this.slider.style.removeProperty('--auto-scroll-frac');
      }
    }

    setup() {
      this.teardown();
      const isMobile = this.mobileQuery.matches;
      const enabledHere = isMobile ? this.enableMobile : this.enableDesktop;
      if (!enabledHere || this.motionQuery.matches || this.originals.length < 2) return;

      /* 切れ目なく流すため、商品を後ろにもう一周分つなげる */
      this.originals.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        clone.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        this.slider.appendChild(clone);
        this.clones.push(clone);
      });
      this.wrapper.classList.add('is-auto-scrolling');
      this.slider.style.scrollSnapType = 'none';
      this.slider.style.scrollBehavior = 'auto';
      this.position = this.slider.scrollLeft;
      this.lastTime = null;

      const step = (now) => {
        if (this.lastTime === null) this.lastTime = now;
        const delta = Math.min(now - this.lastTime, 100);
        this.lastTime = now;
        if (!this.userPaused && this.inView && !document.hidden) {
          this.wrapPosition(this.position + (this.speed * delta) / 1000, false);
        }
        this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    }

    loopWidth() {
      return this.clones[0] ? this.clones[0].offsetLeft - this.originals[0].offsetLeft : 0;
    }

    wrapPosition(value, fromUser) {
      const loop = this.loopWidth();
      let next = value;
      if (loop > 0) {
        if (next >= loop) next -= loop;
        if (fromUser && next <= 0) next += loop;
      }
      this.position = next;
      if (fromUser) {
        this.slider.style.setProperty('--auto-scroll-frac', '0px');
        if (next !== value) this.slider.scrollLeft = next;
        return;
      }
      /* スクロール位置は整数でしか動かないため、端数は表示のずらしで補ってなめらかに流す */
      const whole = Math.floor(next);
      if (this.slider.scrollLeft !== whole) this.slider.scrollLeft = whole;
      this.slider.style.setProperty('--auto-scroll-frac', (next - whole).toFixed(3) + 'px');
    }
  }
  customElements.define('auto-scroll-row', AutoScrollRow);
}
